<template>
  <div class="pb-6">
    <!-- ヘッダー -->
    <v-row>
      <v-col cols="12">
        <h2>
          <v-icon class="mr-2">
            mdi-file-document-outline
          </v-icon>
          EDI申込管理
        </h2>
        <v-spacer class="mt-2" />
      </v-col>
    </v-row>
    <!-- /ヘッダー -->

    <!-- 検索ブロック -->
    <v-row>
      <v-col>
        <v-expansion-panels accordion>
          <v-expansion-panel>
            <v-expansion-panel-header class="panel-header-color panel-selectable">
              <strong>
                <v-icon class="panel-header-color">
                  mdi-magnify
                </v-icon>
                &nbsp;条件を指定して検索
              </strong>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card flat>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="searchForms.tenant_code"
                        dense
                        hide-details="auto"
                        label="テナントコード"
                        persistent-placeholder
                        clearable
                        placeholder="部分一致"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="searchForms.tenant_name"
                        dense
                        label="テナント名"
                        persistent-placeholder
                        clearable
                        placeholder="部分一致"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="searchForms.emission_name"
                        dense
                        label="排出事業者名"
                        persistent-placeholder
                        clearable
                        placeholder="部分一致"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" @click="searchButtonClickEvent">
                    <v-icon>
                      mdi-magnify
                    </v-icon>
                    &nbsp;検索
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <!-- /検索ブロック -->

    <!-- タブとアクションボタン -->
    <v-row align="center">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <!-- 左側: タブ選択エリア -->
          <div class="d-flex align-center">
            <div class="tab-button-container">
              <v-btn
                v-for="tab in tabs"
                :key="tab.value"
                :class="[
                  'tab-button',
                  { 'tab-button-active': activeTab === tab.value }
                ]"
                @click="changeTab(tab.value)"
              >
                {{ tab.text }}
              </v-btn>
            </div>
          </div>

          <!-- 右側: アクションボタン -->
          <div class="d-flex align-center">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="info"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-file-download-outline</v-icon>&nbsp;CSV出力
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="csvExportCharges">
                  <v-list-item-title>JWNET登録用(C料金)</v-list-item-title>
                </v-list-item>
                <v-list-item @click="csvExportJwnet">
                  <v-list-item-title>JWNET登録用(ABC不明)</v-list-item-title>
                </v-list-item>
                <v-list-item @click="csvExportAll">
                  <v-list-item-title>全件出力</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn
              color="info"
              class="ml-2"
              @click="importButtonClickEvent()"
            >
              <v-icon>mdi-file-upload-outline</v-icon>&nbsp;CSV取込
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
    <!-- /タブとアクションボタン -->

    <!-- カウント -->
    <v-row>
      <v-col class="text-left">
        <h4>
          {{ total_count }} 件
        </h4>
      </v-col>
    </v-row>
    <!-- /カウント -->

    <!-- テーブル -->
    <v-row>
      <v-col>
        <v-card class="rounded-card">
          <v-card-text>
            <v-data-table
              v-model="selectedItems"
              :headers="tableHeaders"
              :items="ediRequests"
              :items-per-page="-1"
              disable-pagination
              disable-sort
              hide-default-footer
              show-select
              @click:row="toggleRowSelection"
            >
              <template v-slot:[`item.tenant_code`]="{ item }">
                <div>{{ item.tenant_code }}</div>
                <div class="line-clamp one-clamp">{{ item.tenant_name }}</div>
              </template>
              <template v-slot:[`item.jwnet_name`]="{ item }">
                <div class="line-clamp one-clamp">{{ item.jwnet_name || '-' }}</div>
                <div class="line-clamp one-clamp">{{ item.emission_name}}</div>
              </template>
              <template v-slot:[`item.address`]="{ item }">
                <div class="line-clamp one-clamp">{{ item.address || '-'}} </div>
                <div>{{ item.code }}</div>
              </template>
              <template v-slot:[`item.edi_status`]="{ item }">
                <div :class="getStatusClass(item.edi_status)">{{ item.edi_status | edi_status_label }}</div>
                <div>
                  <span v-if="item.edi_request_at">
                    {{ item.edi_request_at | moment_datetime }}
                  </span>
                  <span v-else> - </span>
              </div>
              </template>
              <template v-slot:[`item.jwnet_pricing_type_name`]="{ item }">
                <div>{{ item.jwnet_pricing_type_name | jwnet_pricing_type_label }}</div>
                <div>{{ item.subscriber_number || '-' }}</div>
              </template>
              <template v-slot:[`item.person_in_charge`]="{ item }">
                <div>{{ item.person_in_charge || '-' }}</div>
                <div v-if="item.updated_at">
                  {{ item.updated_at | unix_datetime_format }}
                </div>
                <div v-else>-</div>
              </template>
              <template v-slot:[`item.actions`]="{item}">
                <template v-if="item.emission_request_id">
                  <template v-if="$vuetify.breakpoint.mdAndUp">
                    <v-btn
                      color="info darken-2"
                      icon
                      @click="showButtonClickEvent(item)"
                    >
                      <v-icon>mdi-file-document-outline</v-icon>
                    </v-btn>
                    <v-btn
                      color="info darken-2"
                      icon
                      @click="editButtonClickEvent(item)"
                    >
                      <v-icon>mdi-note-edit</v-icon>
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn
                      text
                      color="info"
                      class="mr-2"
                      @click="showButtonClickEvent(item)"
                    >
                      参照
                    </v-btn>
                    <v-btn
                      text
                      color="info"
                      @click="editButtonClickEvent(item)"
                    >
                      編集
                    </v-btn>
                  </template>
                </template>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- /テーブル -->

    <!-- ページャ -->
    <v-row
      v-if="hasResult"
      class="mb-14"
    >
      <v-col>
        <v-pagination
          v-model="page"
          :length="pagination.total_pages"
          :total-visible="totalVisible"
          circle
          prev-icon="mdi-menu-left"
          next-icon="mdi-menu-right"
          @input="changePage"
        />
      </v-col>
    </v-row>
    <!-- /ページャ -->

    <!-- 固定フッター -->
    <v-footer fixed class="mb-8" color="white" width="100%;" style="overflow-x: auto;">
      <v-card flat width="100%">
        <v-card-actions>
          <v-spacer />
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                class="mr-3"
                :disabled="selectedItems.length <= 0"
                v-bind="attrs"
                v-on="on"
              >
                ステータス変更
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="changeStatus('mail_send', 'メール送信済')">
                <v-list-item-title>メール送信済</v-list-item-title>
              </v-list-item>
              <v-list-item @click="changeStatus('emission_requested', '排出申込済')">
                <v-list-item-title>排出申込済</v-list-item-title>
              </v-list-item>
              <v-list-item @click="changeStatus('jwnet_requested', 'JWNET登録中')">
                <v-list-item-title>JWNET登録中</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-actions>
      </v-card>
    </v-footer>
    <!-- /固定フッター -->

    <confirm ref="confirm" />
    <csv-export-charges-dialog
      ref="csvExportChargesDialog"
      :message="csvExportDialogMessage"
    />
    <v-snackbar
      v-model="showSnackbar"
      bottom
      color="error"
    >
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import DownloadMixin from "../../../mixins/download-mixin";
import Confirm from '../../../components/Confirm'
import CsvExportChargesDialog from '../../components/CsvExportChargesDialog'

export default {
  name: 'EdiRequestIndex',
  components: {
    Confirm,
    CsvExportChargesDialog,
  },
  mixins: [DownloadMixin],
  data () {
    return {
      page: 1,
      totalVisible: 7,
      total_count: null,
      activeTab: null,
      loading: false,
      csvExportDialogMessage: '',
      showSnackbar: false,
      snackbarMessage: '',
      searchForms: {
        tenant_code: null,
        tenant_name: null,
      },
      tabs: [
        { value: 'mail_send', text: 'メール送信済' },
        { value: 'mail_bounce', text: 'メール送信エラー' },
        { value: 'emission_requested', text: '排出申込済' },
        { value: 'jwnet_requested', text: 'JWNET登録中' },
        { value: 'edi_available', text: 'EDI利用可能' },
        { value: 'edi_completed', text: 'EDI登録完了' },
      ],
      tableHeaders: [
        { value: 'tenant_code', text: 'テナントコード\r\nテナント名', width: "20%" },
        { value: 'jwnet_name', text: 'JWNET登録名\r\n排出事業者名', width: "20%" },
        { value: 'address', text: '住所\r\n事業者コード', width: "20%" },
        { value: 'edi_status', text: 'EDI申込ステータス\r\nEDI申込メール送信日時', width: "10%" },
        { value: 'jwnet_pricing_type_name', text: '料金区分\r\n加入者番号', width: "10%" },
        { value: 'person_in_charge', text: '担当者\r\n更新日時', width: "10%" },
        { text: '操作', value: 'actions', align: 'end', width: "10%" },
      ],
      ediRequests: [],
      selectedItems: [],
    }
  },
  computed: {
    ...mapState('AdminEdiRequest', {
      pagination: 'pagination',
      searchCondition: 'condition',
    }),
    ...mapGetters('AdminEdiRequest', [
      'hasResult'
    ]),
    ...mapGetters('AdminAuthentication', [
      'userName'
    ]),
    currentUserName() {
      return this.userName || '担当者'
    }
  },
  created() {
    if (this.$route.params.resetCondition) {
      this.resetState()
    }
    if (this.$route.params.resetPagination) {
      this.resetPagination()
    }
    this.searchForms.tenant_code = this.searchCondition.tenant_code
    this.searchForms.tenant_name = this.searchCondition.tenant_name
    this.searchForms.emission_name = this.searchCondition.emission_name
    this.page = this.pagination.current_page
    this.activeTab = this.searchCondition.status_tab || 'emission_requested'
    this.searchEdiRequest()
  },
  methods: {
    ...mapActions('AdminEdiRequest', {
      fetchEdiRequests: 'index',
      updateEdiRequestStatus: 'create',
      resetState: 'resetState',
      resetPagination: 'resetPagination',
    }),
    changeTab (tabValue) {
      // 既にアクティブなタブをクリックした場合は選択解除
      if (this.activeTab === tabValue) {
        this.activeTab = null
      } else {
        this.activeTab = tabValue
      }
      this.page = 1
      this.selectedItems = []
      this.searchEdiRequest()
    },
    searchButtonClickEvent () {
      this.page = 1
      this.selectedItems = []
      this.searchEdiRequest()
    },
    showButtonClickEvent (item) {
      if (item.request_type === 'request_new') {
        this.$router.push({
          name: 'EdiRequestUnsubscribedShow',
          params: { id: item.emission_request_id }
        })
      } else {
        this.$router.push({
          name: 'EdiRequestSubscribedShow',
          params: { id: item.emission_request_id }
        })
      }
    },
    editButtonClickEvent (item) {
      if (item.request_type === 'request_new') {
        this.$router.push({
          name: 'EdiRequestUnsubscribedEdit',
          params: { id: item.emission_request_id }
        })
      } else {
        this.$router.push({
          name: 'EdiRequestSubscribedEdit',
          params: { id: item.emission_request_id }
        })
      }
    },
    // CSV出力 - 全件出力
    csvExportAll () {
      let params = this.buildParam('csv', null)
      this.fetchEdiRequests(params).then((content) => {
        if (!content) {
          this.snackbarMessage = '出力対象がありません。'
          this.showSnackbar = true
          return
        }
        this.downloadCsv(content, '全件出力')
      })
    },
    // CSV出力 - JWNET登録用(ABC不明)の出力
    csvExportJwnet () {
      // チェックされたアイテムがない場合はエラー
      if (this.selectedItems.length === 0) {
        const modalOption = {
          title: 'JWNET登録用(ABC不明)の出力',
          message: '対象データを選択してください。',
          okText: 'OK',
          cancelText: null,
          options: { titleColor: 'red' }
        }
        this.$refs.confirm.open(modalOption)
        return
      }

      this.csvExportDialogMessage = '「排出申請済」のJWNET登録用データ（ABC不明）を出力しますか？'
      this.$refs.csvExportChargesDialog.open().then((result) => {
        if (result.confirmed) {
          let params = this.buildParam('csv', 'request_joined')
          // チェックされたIDを追加
          params.emission_ids = this.selectedItems.map(item => item.id)
          this.fetchEdiRequests(params).then((content) => {
            if (!content) {
              this.snackbarMessage = '出力対象がありません。'
              this.showSnackbar = true
              return
            }
            this.downloadCsv(content, 'JWNET登録用(ABC不明)')
            // ステータス更新が選択されている場合、出力対象のデータのステータスを更新
            if (result.updateStatus) {
              this.updateStatusForExportedItems('jwnet_requested', 'request_joined')
            }
          })
        }
      })
    },
    // CSV出力 - JWNET登録用(C料金)の出力
    csvExportCharges () {
      // チェックされたアイテムがない場合はエラー
      if (this.selectedItems.length === 0) {
        const modalOption = {
          title: 'JWNET登録用(C料金)の出力',
          message: '対象データを選択してください。',
          okText: 'OK',
          cancelText: null,
          options: { titleColor: 'red' }
        }
        this.$refs.confirm.open(modalOption)
        return
      }

      this.csvExportDialogMessage = '「排出申請済」のJWNET登録用データ（C料金）を出力しますか？'
      this.$refs.csvExportChargesDialog.open().then((result) => {
        if (result.confirmed) {
          let params = this.buildParam('csv', 'request_new')
          // チェックされたIDを追加
          params.emission_ids = this.selectedItems.map(item => item.id)
          this.fetchEdiRequests(params).then((content) => {
            if (!content) {
              this.snackbarMessage = '出力対象がありません。'
              this.showSnackbar = true
              return
            }
            this.downloadSJISCsv(content, 'JWNET登録用(C料金)')
            // ステータス更新が選択されている場合、出力対象のデータのステータスを更新
            if (result.updateStatus) {
              this.updateStatusForExportedItems('jwnet_requested', 'request_new')
            }
          })
        }
      })
    },
    // CSV取込
    importButtonClickEvent () {
      this.$router.push({ name: 'AdminEdiRequestCsvImport' })
    },
    changePage () {
      this.searchEdiRequest()
    },
    searchEdiRequest () {
      if (this.loading) {
        return
      }

      this.loading = true
      let params = this.buildParam("json", null)
      this.fetchEdiRequests(params)
        .then((content) => {
          this.ediRequests = content.edi_requests || []
          this.total_count = content.pagination.total_count || 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    /*パラメータ設定 */
    buildParam(format, requestType) {
      return {
        tenant_code: this.searchForms.tenant_code,
        tenant_name: this.searchForms.tenant_name,
        emission_name: this.searchForms.emission_name,
        status_tab: this.activeTab,
        request_type: requestType,
        limit: 100,
        page: this.page,
        format: format,
      };
    },
    getStatusClass (status) {
      if (status && status.includes('エラー')) {
        return 'error--text'
      }
      return ''
    },
    changeStatus (status, statusLabel) {
      if (this.selectedItems.length === 0) {
        return
      }

      const modalOption = {
        title: '確認',
        message: `選択した${this.selectedItems.length}件を「${statusLabel}」に変更しますか？`,
        okText: '変更する',
        cancelText: 'キャンセル'
      }

      this.$refs.confirm.open(modalOption).then((confirm) => {
        if (confirm) {
          this.updateStatus(status)
        }
      })
    },
    updateStatus (status) {
      const emission_ids = this.selectedItems.map(item => item.id)
      const params = {
        emission_ids: emission_ids,
        status: status
      }

      this.updateEdiRequestStatus({ params: params })
        .then(() => {
          this.selectedItems = []
          this.searchEdiRequest()
        })
    },
    updateStatusForExportedItems (status, requestType) {
      // チェックされたアイテムのIDを取得
      const emission_ids = this.selectedItems
        .filter(item => item.id)
        .map(item => item.id)

      if (emission_ids.length === 0) {
        return
      }

      const updateParams = {
        emission_ids: emission_ids,
        request_type: requestType,
        status: status,
        format: 'export'
      }
      
      // ステータス更新実行
      this.updateEdiRequestStatus({ params: updateParams }).then(() => {
        this.searchEdiRequest()
      })
    },
    toggleRowSelection (item) {
      const index = this.selectedItems.findIndex(row => row === item)
      if (index > -1) {
        // 既に選択されている場合は削除
        this.selectedItems.splice(index, 1)
      } else {
        // 選択されていない場合は追加
        this.selectedItems.push(item)
      }
    },
  }
}
</script>

<style scoped>
.error--text {
  color: #d32f2f !important;
}

.tab-button-container {
  display: inline-flex;
  gap: 0;
}

.tab-button {
  border-radius: 0 !important;
  margin: 0 !important;
  min-width: 140px !important;
  width: 140px !important;
  height: 36px !important;
  padding: 8px 12px !important;
  font-weight: 500;
  font-size: 13px;
  background-color: white !important;
  color: #666 !important;
  border: 1px solid #d0d0d0;
  border-right: none;
  box-shadow: none !important;
}

.tab-button:first-child {
  border-top-left-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
}

.tab-button:last-child {
  border-right: 1px solid #d0d0d0 !important;
  border-top-right-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
}

.tab-button-active {
  background-color: #0d74ba !important;
  color: white !important;
  border-color: #0d74ba !important;
}

.tab-button:hover:not(.tab-button-active) {
  background-color: #fafafa !important;
}
</style>
